// client/src/App.tsx
import { useEffect, useState } from 'react';
// @ts-ignore
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from './utils/mui-imports';

import LoginScreen from './components/LoginScreen.tsx';
import ShiftSelection from './components/ShiftSelection.tsx';
import OAuthCallback from './components/OAuthCallback.tsx';
import LegalDocument from './components/LegalDocument.tsx';
import { 
  DispatcherInterface, 
  UnitInterface, 
  RadioPage,
  BigBoard
} from './components/LazyComponents.tsx';

import { SocketProvider, useSocket, useSocketStatus } from './contexts/SocketContext.tsx';
import { AuthProvider, useAuth } from './hooks/useAuth.tsx';
import { ToastProvider, useToast } from './contexts/ToastContext.tsx';
import { ConfigProvider } from './contexts/ConfigContext.tsx';
import { preloadCriticalComponents, preloadRoleComponents } from './utils/preloadUtils';
import { http } from './lib/http';
import { requestUnitUnclaim, sendUnitUnclaimBeacon } from './utils/unitUnclaim';

// Dark theme matching Config.lua exactly
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#3c8cff' },
    secondary: { main: '#ff6e6e' },
    background: { default: '#101216', paper: '#1c1f25' },
    text: { primary: '#eceef1', secondary: '#b4b9c0' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', fontSize: 14 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1a1d22',
            '& fieldset': { borderColor: '#464a52' },
            '&:hover fieldset': { borderColor: '#3c8cff' },
            '&.Mui-focused fieldset': { borderColor: '#3c8cff' },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1d22',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#464a52' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3c8cff' },
        },
      },
      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              backgroundColor: '#1c1f25',
              border: '1px solid #40444b',
              '& .MuiMenuItem-root': {
                color: '#eceef1',
                '&:hover': {
                  backgroundColor: '#2a2d32',
                },
                '&.Mui-selected': {
                  backgroundColor: '#3c8cff',
                  '&:hover': {
                    backgroundColor: '#2a7ae9',
                  },
                },
              },
            },
          },
          // Ensure dropdown appears above other elements
          style: {
            zIndex: 9999,
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundColor: '#1c1f25', border: '1px solid #40444b' } } },
    MuiCard: { styleOverrides: { root: { backgroundColor: '#1c1f25', border: '1px solid #40444b' } } },
  },
});

function AppContent() {
  const { user, isAuthenticated, loading } = useAuth();
  const socket = useSocket();
  const { connected } = useSocketStatus();
  const { showInfo, showWarning } = useToast();
  const [shiftData, setShiftData] = useState<{
    callsign: string;
    role: string;
    unitId?: string;
  } | null>(() => {
    // Load shift data from localStorage on component mount
    const savedShiftData = localStorage.getItem('cad_shift_data');
    return savedShiftData ? JSON.parse(savedShiftData) : null;
  });

  // Join/leave the dispatch hub based on auth + socket state
  useEffect(() => {
    if (!socket) return;

    const join = () => {
      if (isAuthenticated && socket.connected) {
        console.log('[App] Joining dispatch room...');
        socket.emit('join-dispatch');
      } else {
        console.log('[App] Cannot join dispatch room - authenticated:', isAuthenticated, 'connected:', socket?.connected);
      }
    };

    // try now, and again on reconnect
    join();
    socket.on('connect', join);

    return () => {
      try {
        if (isAuthenticated) socket.emit('leave-dispatch');
      } catch {}
      socket.off('connect', join);
    };
  }, [socket, isAuthenticated]);

  // Preload critical components when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      preloadCriticalComponents();
    }
  }, [isAuthenticated]);

  // Listen for user assignment changes
  useEffect(() => {
    if (!socket || !user) return;

    const handleUserAssignmentChanged = (data: any) => {
      // Only react if this is for the current user
      if (data.userId !== user.id) return;

      console.log('[App] User assignment changed:', data);
      
      // Show toast notification
      showInfo(`You have been assigned to unit ${data.unitId}`);
      
      // Automatically claim the assigned unit
      http.post('/auth/claim-unit', { unitId: data.unitId })
        .then(() => {
          console.log('[App] Successfully claimed assigned unit:', data.unitId);
          showInfo(`You are now on duty as unit ${data.unitId}`);
        })
        .catch((error: any) => {
          console.error('[App] Failed to claim assigned unit:', error);
          showWarning('Failed to claim assigned unit. Please try refreshing the page.');
        });
    };

    const handleUserAssignmentRemoved = (data: any) => {
      // Only react if this is for the current user
      if (data.userId !== user.id) return;

      console.log('[App] User assignment removed:', data);
      
      // Show toast notification
      showWarning('Your unit assignment has been removed');
    };

    socket.on('user-assignment-changed', handleUserAssignmentChanged);
    socket.on('user-assignment-removed', handleUserAssignmentRemoved);

    return () => {
      socket.off('user-assignment-changed', handleUserAssignmentChanged);
      socket.off('user-assignment-removed', handleUserAssignmentRemoved);
    };
  }, [socket, user]);

  const handleShiftSelected = (data: { callsign: string; role: string; unitId?: string }) => {
    setShiftData(data);
    // Save shift data to localStorage for persistence
    localStorage.setItem('cad_shift_data', JSON.stringify(data));
    // Preload components for the selected role
    preloadRoleComponents(data.role);
  };

  const handleReturnToShiftSelection = async () => {
    await requestUnitUnclaim('shift-return');
    sendUnitUnclaimBeacon('shift-return');
    
    // Clear local shift data
    setShiftData(null);
    localStorage.removeItem('cad_shift_data');
    localStorage.removeItem('claimed_unit_id');
    localStorage.removeItem('claimed_unit_role');
  };

  const renderInterface = () => {
    if (!user) return null;

    // Show shift selection if user hasn't selected a shift yet
    if (!shiftData) {
      return <ShiftSelection onShiftSelected={handleShiftSelected} />;
    }

    // Optional: show a small loader while socket is connecting
    if (!connected) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
          <CircularProgress />
          <Typography sx={{ color: '#eceef1' }}>Connecting to CAD server…</Typography>
        </Box>
      );
    }

    // Render the appropriate CAD interface based on selected role
    switch (shiftData.role) {
      case 'admin':
        // Admin role is now accessed via button in other interfaces, not as standalone
        // This case should never be reached due to filtering in ShiftSelection
        return <div>Admin access is now available via the admin button in Unit/Dispatcher interfaces</div>;
      case 'dispatcher':
        return <DispatcherInterface onReturnToShiftSelection={handleReturnToShiftSelection} />;
      case 'command':
        return <UnitInterface onReturnToShiftSelection={handleReturnToShiftSelection} />;
      case 'unit':
        return <UnitInterface onReturnToShiftSelection={handleReturnToShiftSelection} />;
      default:
        return <div>Unknown role: {shiftData.role}</div>;
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: '#101216',
        color: '#eceef1',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
          <CircularProgress />
          <Typography sx={{ color: '#eceef1' }}>Verifying authentication...</Typography>
        </Box>
      ) : !isAuthenticated ? (
        <LoginScreen />
      ) : (
        renderInterface()
      )}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <SocketProvider>
          <AuthProvider>
            <ConfigProvider>
              <ToastProvider>
                <Routes>
                  <Route path="/radio" element={<RadioPage />} />
                  <Route path="/big-board" element={<BigBoard />} />
                  <Route path="/auth/callback" element={<OAuthCallback />} />
                  <Route path="/legal/privacy" element={<LegalDocument type="privacy" />} />
                  <Route path="/legal/terms" element={<LegalDocument type="terms" />} />
                  <Route path="/*" element={<AppContent />} />
                </Routes>
              </ToastProvider>
            </ConfigProvider>
          </AuthProvider>
        </SocketProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
